// src/services/api.ts
import axios from 'axios';
import { Question, CreateQuestionDto, UpdateQuestionDto, QuestionFilterDto, QuestionReviewDto, QuestionStatsUpdateDto } from '../types/question';
import { config } from '../config';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lavernaibackend.onrender.com/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('API Request:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      data: config.data
    });
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    return response;
  },
  async (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });

    const originalRequest = error.config;

    // Only handle 401 errors for token refresh, ignore other errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh the token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success') {
              // Update tokens in localStorage
              localStorage.setItem('authToken', data.token);
              localStorage.setItem('refreshToken', data.refreshToken);
              
              // Update the Authorization header for the original request
              originalRequest.headers.Authorization = `Bearer ${data.token}`;
              
              // Retry the original request
              return apiClient(originalRequest);
            }
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }

      // If refresh failed or no refresh token, only clear tokens if we're not on auth pages
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/auth/')) {
        // Don't redirect immediately, let the component handle it
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authUser');
      }
    }
    
    return Promise.reject(error);
  }
);

class API {
  private async request<T>(endpoint: string, options: any = {}): Promise<T> {
    try {
      const response = await apiClient({
        url: endpoint,
        ...options,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(`API request failed: ${error.response?.data?.message || error.message}`);
    }
  }

  // Question endpoints
  async createQuestion(questionData: CreateQuestionDto): Promise<Question> {
    return this.request<Question>('/questions', {
      method: 'POST',
      data: questionData,
    });
  }

  async updateQuestion(id: string, dto: UpdateQuestionDto): Promise<Question> {
    return this.request<Question>(`/questions/${id}`, {
      method: 'PATCH',
      data: dto,
    });
  }

  async getQuestions(filter: QuestionFilterDto): Promise<Question[]> {
    const queryParams = new URLSearchParams();
    if (filter.subject) queryParams.append('subject', filter.subject);
    if (filter.strand) queryParams.append('strand', filter.strand);
    if (filter.substrand) queryParams.append('substrand', filter.substrand);
    if (filter.tags) filter.tags.forEach(tag => queryParams.append('tags', tag));
    if (filter.learningObjectives) filter.learningObjectives.forEach(obj => queryParams.append('learningObjectives', obj));
    if (filter.prerequisites) filter.prerequisites.forEach(prereq => queryParams.append('prerequisites', prereq));

    return this.request<Question[]>(`/questions?${queryParams.toString()}`);
  }

  async getQuestionById(id: string): Promise<Question> {
    return this.request<Question>(`/questions/${id}`);
  }

  async deleteQuestion(id: string): Promise<void> {
    return this.request<void>(`/questions/${id}`, {
      method: 'DELETE',
    });
  }

  async reviewQuestion(id: string, review: QuestionReviewDto): Promise<Question> {
    return this.request<Question>(`/questions/${id}/review`, {
      method: 'POST',
      data: review,
    });
  }

  async updateQuestionStats(id: string, stats: QuestionStatsUpdateDto): Promise<Question> {
    return this.request<Question>(`/questions/${id}/stats`, {
      method: 'PATCH',
      data: stats,
    });
  }

  async evaluateAnswer(questionId: string, answer: string): Promise<any> {
    return this.request<any>(`/questions/${questionId}/evaluate`, {
      method: 'POST',
      data: { answer },
    });
  }

  async addTeacher(schoolId: string, teacherData: {
    firstName: string;
    lastName: string;
    email: string;
    schoolId: string;
  }): Promise<any> {
    return this.request<any>(`/admin/schools/${schoolId}/teachers`, {
      method: 'POST',
      data: teacherData,
    });
  }
}

export const api = new API();

export const fetchRecentActivities = async () => {
  try {
    const response = await apiClient.get('/activities/recent');
    return response.data;
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    throw error;
  }
};

export const fetchNotifications = async () => {
  try {
    const response = await apiClient.get('/notifications');
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const fetchStudySessionOptions = async () => {
  try {
    const response = await apiClient.get('/study-session/options');
    return response.data;
  } catch (error) {
    console.error('Error fetching study session options:', error);
    throw error;
  }
};

export const fetchLearningGoals = async () => {
  try {
    const response = await apiClient.get('/goals');
    return response.data;
  } catch (error) {
    console.error('Error fetching learning goals:', error);
    throw error;
  }
};

export default apiClient;
// Add additional API calls as needed
